import * as React from 'react';
import { Row, Col } from 'react-bootstrap';
import { Field } from 'redux-form';

import type VendorVM from 'ab-viewModels/vendor/vendor.viewModel';

import type { VendorPackageTypeItem } from '@acceligentllc/shared/enums/vendorPackageType';
import { VendorList } from '@acceligentllc/shared/enums/vendorPackageType';

import AsyncSelect from 'af-fields/AsyncSelect';
import Dropdown from 'af-fields/Dropdown';
import Input from 'af-fields/Input';
import Textarea from 'af-fields/Textarea';

type Props = {
	vendors: { label: string; value: number; }[];
	onVendorChange: (name: Nullable<string>) => void;
};

const getVendorValue = (option: Props['vendors'][0]) => option.value;
const getVendorLabel = (option: Props['vendors'][0]) => option.label;
const formatVendorLabel = (option: Props['vendors'][0]) => <span>{option.label}</span>;

const Vendor = (props: Props) => {
	const { vendors, onVendorChange } = props;

	const [isVendorSelected, setIsVendorSelected] = React.useState(false);

	const filterVendor = React.useCallback(async (key: string) => {
		if (!key) {
			return vendors;
		}
		return vendors.filter((_item) => {
			const name = _item.label.toLowerCase();
			return name.includes(key.toLowerCase());
		});
	}, [vendors]);

	const onVendorValueChange = React.useCallback(() => {
		setIsVendorSelected(true);
	}, []);

	const onVendorCreate = React.useCallback((name: string) => {
		onVendorChange(name);
	}, [onVendorChange]);

	const onVendorClear = React.useCallback(() => {
		onVendorChange(null);
		setIsVendorSelected(false);
	}, [onVendorChange]);

	const isNewVendorValid = React.useCallback((
		inputValue: string
	) => {
		if (!inputValue) {
			return false;
		}
		return !vendors.some((_item) => _item.label.toLowerCase() === inputValue.toLowerCase());
	}, [vendors]);

	const getNewVendor = React.useCallback((value: string, label: string) => {
		return { id: +value, name: label } as VendorVM;
	}, []);

	// fixes issue where vendor list is not shown correctly after creating new option and clearing it.
	// Props would update correctly, but new value wouldn't be shown
	// filtering by new vendor name would show it correctly.
	const getOptions = React.useCallback(() => {
		return vendors;
	}, [vendors]);

	const packageTypeItem = React.useCallback((option: VendorPackageTypeItem) => <div>{option.label}</div>, []);

	return (
		<Row>
			<Col md={8}>
				<Field
					allowNew={true}
					component={AsyncSelect}
					formatOptionLabel={formatVendorLabel}
					getNewOptionData={getNewVendor}
					getOptionLabel={getVendorLabel}
					getOptionValue={getVendorValue}
					isValidNewOption={isNewVendorValid}
					label="Vendor"
					name="vendor"
					onClear={onVendorClear}
					onCreateNew={onVendorCreate}
					onSearch={filterVendor}
					onValueChange={onVendorValueChange}
					options={getOptions()}
					placeholder="Select Vendor"
				/>
			</Col>
			<Col md={8}>
				<Field
					component={Input}
					controlCursor={true}
					disableErrors={false}
					forceShowError={true}
					hideErrorText={false}
					id="vendorPartNumber"
					label={`Vendor Part Number${isVendorSelected ? ' *' : ''}`}
					name="vendorPartNumber"
					placeholder="Enter Vendor Part Number"
					type="text"
				/>
			</Col>
			<Col md={8}>
				<Field
					addonAfter="$"
					allowNegative={false}
					component={Input}
					controlCursor={true}
					disableErrors={false}
					forceShowError={true}
					hideErrorText={false}
					isRequired={isVendorSelected}
					isZeroIfEmpty={true}
					label="Vendor Price"
					name="vendorPrice"
					placeholder="Enter Vendor Price"
					type="number"
					withThousandsSeparator={true}
				/>
			</Col>
			<Col md={8}>
				<Field
					component={Input}
					controlCursor={true}
					label="Vendor Url"
					name="vendorUrl"
					placeholder="Enter Vendor Url"
					type="text"
				/>
			</Col>
			<Col md={8}>
				<Field
					component={Dropdown}
					id="packageType"
					label="Package Type"
					name="packageType"
					options={VendorList}
					placeholder="Select Package Type"
					renderMenuItem={packageTypeItem}
					valueKey="id"
					withCaret={true}
				/>
			</Col>
			<Col md={8}>
				<Field
					component={Input}
					controlCursor={true}
					label="Package Quantity"
					name="packageQuantity"
					placeholder="Enter Package Quantity"
					type="text"
				/>
			</Col>
			<Col md={24}>
				<Field
					component={Textarea}
					controlCursor={true}
					label="Notes"
					name="note"
					placeholder="Enter Notes"
					rows={3}
				/>
			</Col>
		</Row>
	);
};

export default Vendor;
